import type { CalendarDate } from '../value-objects/CalendarDate';
import type { DateRange } from '../value-objects/DateRange';

/**
 * How many working days an absence actually costs.
 *
 * A weekend is never a business day. A public holiday is not a business day
 * *when the holiday calendar is switched on* — the `feriados` feature flag
 * decides that, and when it is off the caller passes an empty set. The policy
 * itself takes the set of non-working dates as an argument rather than reading
 * configuration, which is what keeps it pure and exhaustively testable.
 *
 * This is the single most consequential calculation in the system: it determines
 * how many days come off a person's balance, so an off-by-one here is money.
 */

/** The set of `YYYY-MM-DD` strings that do not count as working days. */
export type NonWorkingDates = ReadonlySet<string>;

export const NO_HOLIDAYS: NonWorkingDates = new Set<string>();

export interface BusinessDayBreakdown {
  readonly businessDays: number;
  readonly weekendDays: number;
  /** Holidays that fell on a weekday and were therefore actually deducted. */
  readonly holidayDays: number;
  readonly naturalDays: number;
  /** The specific dates deducted as holidays, for the wizard's day-by-day hints. */
  readonly deductedHolidays: readonly string[];
}

/**
 * Whether one date is a working day.
 *
 * Order matters for the *breakdown*, not for the total: a holiday falling on a
 * Saturday is counted as a weekend day and not double-deducted. Mexican statutory
 * holidays are largely moved to Mondays precisely to avoid this, but customary
 * days such as 2 November land wherever they land.
 */
export const isBusinessDay = (date: CalendarDate, nonWorking: NonWorkingDates): boolean =>
  !date.isWeekend && !nonWorking.has(date.toISO());

/**
 * Business days in a range, both ends inclusive. Returns 0 for a range that
 * contains no working day at all — a request covering only a weekend is not a
 * negative number of days, it is a request that should be rejected upstream.
 */
export const countBusinessDays = (range: DateRange, nonWorking: NonWorkingDates): number => {
  let count = 0;
  for (const day of range.days()) {
    if (isBusinessDay(day, nonWorking)) count += 1;
  }
  return count;
};

/**
 * The same count with its components broken out. The wizard shows "10 días
 * hábiles (descontando 1 feriado)", which needs to know which days were removed
 * and why.
 */
export const describeBusinessDays = (
  range: DateRange,
  nonWorking: NonWorkingDates,
): BusinessDayBreakdown => {
  let businessDays = 0;
  let weekendDays = 0;
  let holidayDays = 0;
  const deductedHolidays: string[] = [];

  for (const day of range.days()) {
    if (day.isWeekend) {
      weekendDays += 1;
      continue;
    }
    const iso = day.toISO();
    if (nonWorking.has(iso)) {
      holidayDays += 1;
      deductedHolidays.push(iso);
      continue;
    }
    businessDays += 1;
  }

  return {
    businessDays,
    weekendDays,
    holidayDays,
    naturalDays: range.naturalDays,
    deductedHolidays,
  };
};

/**
 * The next working day strictly after `date`. Used for "Regresas el …", which
 * must skip a weekend and a holiday alike — telling someone they return on a
 * Saturday would be wrong.
 *
 * Bounded so a pathological holiday calendar cannot spin forever; a year of
 * consecutive non-working days means the configuration is broken, and returning
 * the input is a visible symptom rather than a hang.
 */
export const nextBusinessDay = (date: CalendarDate, nonWorking: NonWorkingDates): CalendarDate => {
  const MAX_LOOKAHEAD_DAYS = 366;
  let cursor = date.addDays(1);
  for (let step = 0; step < MAX_LOOKAHEAD_DAYS; step += 1) {
    if (isBusinessDay(cursor, nonWorking)) return cursor;
    cursor = cursor.addDays(1);
  }
  return date;
};

/** The first working day at or after `date`. */
export const firstBusinessDayFrom = (
  date: CalendarDate,
  nonWorking: NonWorkingDates,
): CalendarDate => (isBusinessDay(date, nonWorking) ? date : nextBusinessDay(date, nonWorking));

/**
 * Walks forward from `start` until `count` working days have been gathered, and
 * returns the last of them. This is how a window of a requested length is
 * proposed: "the same 10 business days, starting the 25th" has to land on a
 * working day at both ends.
 *
 * Returns null when `count` is not positive, so a caller cannot accidentally
 * build an inverted range.
 */
export const endOfBusinessWindow = (
  start: CalendarDate,
  count: number,
  nonWorking: NonWorkingDates,
): CalendarDate | null => {
  if (count <= 0) return null;
  const MAX_SPAN_DAYS = 366 * 2;

  let gathered = 0;
  let cursor = start;

  for (let step = 0; step < MAX_SPAN_DAYS; step += 1) {
    if (isBusinessDay(cursor, nonWorking)) {
      gathered += 1;
      if (gathered === count) return cursor;
    }
    cursor = cursor.addDays(1);
  }
  return null;
};

/**
 * Whether a request gives enough notice. Measured in calendar days from today to
 * the first day off, which is how a person reads a notice period — "15 days
 * before" means two weeks on the calendar, not three working weeks.
 */
export const hasSufficientNotice = (
  today: CalendarDate,
  start: CalendarDate,
  requiredNoticeDays: number,
): boolean => today.daysUntil(start) >= requiredNoticeDays;

/** Days of notice a range actually gives; negative when the start is past. */
export const noticeDaysGiven = (today: CalendarDate, start: CalendarDate): number =>
  today.daysUntil(start);
